import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { formatDate } from "lopermut"
import { usePredefinedResponsesActivityQuery } from "modules/insights/hooks/usePredefinedResponsesActivityQuery"
import { ACTIVITY_BAR_SIZE } from "modules/shared/lib/charts"
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

type Props = {
  campaignId?: string
  goalId: string
}

export const PredefinedResponsesActivity = ({ campaignId, goalId }: Props) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)

  const { data, isPending } = usePredefinedResponsesActivityQuery({
    campaignId,
    goalId,
  })

  const chartData = useMemo(() => {
    if (!data?.dailyCount)
      return {
        data: [],
        goalNames: {
          top1: null,
          top2: null,
          top3: null,
          top4: null,
          top5: null,
        },
        showOthers: false,
      }

    // First, calculate total conversations for each campaign across all dates
    const goalTotals = new Map<string, { optionValue: string; total: number }>()

    data.dailyCount.forEach((dayData) => {
      dayData.goals.forEach((goal) => {
        const existing = goalTotals.get(goal.optionValue)
        if (existing) {
          existing.total += goal.responses
        } else {
          goalTotals.set(goal.optionValue, {
            optionValue: goal.optionValue,
            total: goal.responses,
          })
        }
      })
    })

    const totalOptionValues = goalTotals.size
    const showOthers = totalOptionValues > 5

    // Get top 5 campaigns by total conversations
    const top5Goals = Array.from(goalTotals.values())
      .sort((a, b) => a.optionValue.localeCompare(b.optionValue))
      .slice(0, 5)

    // Store campaign names and IDs for legend
    const goalNames = {
      top1: top5Goals[0] ? { name: top5Goals[0].optionValue } : null,
      top2: top5Goals[1] ? { name: top5Goals[1].optionValue } : null,
      top3: top5Goals[2] ? { name: top5Goals[2].optionValue } : null,
      top4: top5Goals[3] ? { name: top5Goals[3].optionValue } : null,
      top5: top5Goals[4] ? { name: top5Goals[4].optionValue } : null,
    }

    // Now map daily data
    const dailyData = data.dailyCount.map((item) => {
      const goalMap = new Map(item.goals.map((g) => [g.optionValue, g.responses]))

      const base = {
        formattedDate: item.date ? formatDate(item.date, "MMM d") : null,
        date: item.date ? formatDate(item.date) : null,
        top1: top5Goals[0] ? (goalMap.get(top5Goals[0].optionValue) ?? 0) : 0,
        top2: top5Goals[1] ? (goalMap.get(top5Goals[1].optionValue) ?? 0) : 0,
        top3: top5Goals[2] ? (goalMap.get(top5Goals[2].optionValue) ?? 0) : 0,
        top4: top5Goals[3] ? (goalMap.get(top5Goals[3].optionValue) ?? 0) : 0,
        top5: top5Goals[4] ? (goalMap.get(top5Goals[4].optionValue) ?? 0) : 0,
      }

      if (showOthers) {
        return {
          ...base,
          others: item.goals
            .filter((g) => !top5Goals.map((tg) => tg.optionValue).includes(g.optionValue))
            .reduce((sum, goal) => sum + goal.responses, 0),
        }
      }
      return base
    })

    return {
      data: dailyData,
      goalNames,
      showOthers,
    }
  }, [data])

  const chartDataByDate = useMemo(() => {
    return chartData.data.reduce(
      (acc, item) => {
        acc[item.date ?? ""] = item
        return acc
      },
      {} as Record<string, (typeof chartData.data)[0]>,
    )
  }, [chartData])

  useEffect(() => {
    if (containerRef.current && chartData?.data?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  const noData = useMemo(() => {
    if (isPending) return false
    return data?.dailyCount.every((el) => el.goals.every((goal) => goal.responses === 0))
  }, [isPending, data])

  const chartConfig = {} satisfies ChartConfig

  return (
    <ChartCard
      title={t("insights.predefinedResponses.activity.title")}
      description={t("insights.predefinedResponses.activity.description")}
      chart={
        <ChartContainer
          config={chartConfig}
          className="h-88"
          style={{
            width: `${(chartData?.data?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
            minWidth: "100%",
          }}
        >
          <BarChart accessibilityLayer data={chartData.data} margin={{ bottom: 10 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(val) => chartDataByDate[val]?.formattedDate ?? ""}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              width={30}
              allowDataOverflow={true}
              hide={true}
            />
            <ChartTooltip
              content={(props) => {
                const filteredPayload = props.payload
                  ?.map((item) => ({
                    ...item,
                    name:
                      item.dataKey === "others"
                        ? t("insights.predefinedResponses.others")
                        : chartData.goalNames[item.dataKey as keyof typeof chartData.goalNames]
                            ?.name,
                  }))
                  .filter((item) => item.name != null)

                return <ChartTooltipContent {...props} payload={filteredPayload} />
              }}
            />
            <Bar
              name={chartData.goalNames.top1?.name}
              dataKey="top1"
              stackId="a"
              fill={`var(--chart-1)`}
              barSize={ACTIVITY_BAR_SIZE}
            />
            <Bar
              name={chartData.goalNames.top2?.name}
              dataKey="top2"
              stackId="a"
              fill={`var(--chart-2)`}
              barSize={ACTIVITY_BAR_SIZE}
            />
            <Bar
              name={chartData.goalNames.top3?.name}
              dataKey="top3"
              stackId="a"
              fill={`var(--chart-3)`}
              barSize={ACTIVITY_BAR_SIZE}
            />
            <Bar
              name={chartData.goalNames.top4?.name}
              dataKey="top4"
              stackId="a"
              fill={`var(--chart-4)`}
              barSize={ACTIVITY_BAR_SIZE}
            />
            <Bar
              name={chartData.goalNames.top5?.name}
              dataKey="top5"
              stackId="a"
              fill={`var(--chart-primary)`}
              barSize={ACTIVITY_BAR_SIZE}
            />
            {chartData.showOthers && (
              <Bar
                name={t("insights.predefinedResponses.others")}
                dataKey="others"
                stackId="a"
                fill={`var(--chart-secondary)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
            )}
          </BarChart>
        </ChartContainer>
      }
      isPending={isPending}
      noData={{
        show: noData,
        title: t("insights.predefinedResponses.noData.title"),
        description: t("insights.predefinedResponses.noData.description"),
      }}
    />
  )
}
